import React, { useMemo, useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType
} from 'reactflow';
import { User, Calendar, MapPin, Users } from 'lucide-react';

// Heritage color palette
const COLORS = {
  heritageGreen: '#2C4A3B',
  warmSand: '#E6E2D3',
  goldAccent: '#C9A227',
  darkText: '#1a1a1a',
  lightText: '#f5f5f5',
  maleNode: '#2C4A3B',
  femaleNode: '#4A2C3B',
  unknownNode: '#3B3B2C'
};

// Custom node for Descendant Chart
const DescendantNode = ({ data }) => {
  const bgColor = data.gender === 'male' ? COLORS.maleNode : 
                  data.gender === 'female' ? COLORS.femaleNode : 
                  COLORS.unknownNode;
  
  return (
    <div 
      className="relative rounded-lg shadow-lg border-2 transition-all duration-200 hover:shadow-xl"
      style={{ 
        backgroundColor: bgColor,
        borderColor: data.isRoot ? COLORS.goldAccent : 'rgba(255,255,255,0.2)',
        borderWidth: data.isRoot ? '3px' : '1px',
        minWidth: '160px',
        maxWidth: '180px'
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: COLORS.goldAccent }} />
      <Handle type="source" position={Position.Bottom} style={{ background: COLORS.goldAccent }} />
      
      <div className="p-3">
        {/* Photo or Avatar */}
        <div className="flex items-center gap-2 mb-2">
          {data.photoUrl ? (
            <img 
              src={data.photoUrl} 
              alt={data.name}
              className="w-8 h-8 rounded-full object-cover border"
              style={{ borderColor: COLORS.warmSand }}
            />
          ) : (
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              <User className="w-4 h-4" style={{ color: COLORS.warmSand }} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate" style={{ color: COLORS.warmSand }}>
              {data.name || 'Unknown'}
            </p>
            {data.generation !== undefined && (
              <p className="text-[9px] opacity-60" style={{ color: COLORS.warmSand }}>
                Gen {data.generation}
              </p>
            )}
          </div>
        </div>
        
        {/* Details */}
        <div className="space-y-0.5 text-[10px]" style={{ color: COLORS.warmSand }}>
          {data.birthYear && (
            <div className="flex items-center gap-1 opacity-80">
              <Calendar className="w-2.5 h-2.5" />
              <span>b. {data.birthYear}</span>
              {data.deathYear && <span>- d. {data.deathYear}</span>}
            </div>
          )}
          {data.childCount > 0 && (
            <div className="flex items-center gap-1 opacity-70">
              <Users className="w-2.5 h-2.5" />
              <span>{data.childCount} {data.childCount === 1 ? 'child' : 'children'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { descendant: DescendantNode };

// Build descendant tree structure
const buildDescendantLayout = (individuals, relationships, rootId, maxGenerations = 6) => {
  const nodes = [];
  const edges = [];
  
  const indById = {};
  individuals.forEach(ind => { indById[ind.id] = ind; });
  
  // Build child lookup
  const parentToChildren = {};
  relationships.forEach(rel => {
    if (rel.relationship_category === 'parent_child' && rel.child_id && rel.parent_id) {
      if (!parentToChildren[rel.parent_id]) {
        parentToChildren[rel.parent_id] = [];
      }
      parentToChildren[rel.parent_id].push(rel.child_id);
    }
  });
  
  // Helper to extract year
  const extractYear = (dateStr) => {
    if (!dateStr) return null;
    const match = dateStr.match(/(\d{4})/);
    return match ? parseInt(match[1]) : null;
  };
  
  // Calculate subtree width for positioning
  const subtreeWidth = {};
  const calculateWidth = (personId, visited = new Set()) => {
    if (visited.has(personId)) return 0;
    visited.add(personId);
    
    const children = parentToChildren[personId] || [];
    if (children.length === 0) {
      subtreeWidth[personId] = 1;
      return 1;
    }
    
    let width = 0;
    children.forEach(childId => {
      width += calculateWidth(childId, visited);
    });
    subtreeWidth[personId] = Math.max(width, 1);
    return subtreeWidth[personId];
  };
  calculateWidth(rootId);
  
  // DFS to build tree
  const visited = new Set();
  const nodeSpacing = 200;
  const levelHeight = 180;
  
  const addNode = (personId, generation, xOffset, parentX) => {
    if (visited.has(personId) || generation > maxGenerations) return;
    visited.add(personId);
    
    const ind = indById[personId];
    if (!ind) return;
    
    const children = parentToChildren[personId] || [];
    const width = subtreeWidth[personId] || 1;
    const x = xOffset + (width * nodeSpacing) / 2;
    const y = generation * levelHeight;
    
    const name = `${ind.given_names || ''} ${ind.surname || ''}`.trim();
    
    nodes.push({
      id: ind.id,
      type: 'descendant',
      position: { x, y },
      data: {
        name,
        gender: ind.sex ?? ind.gender,
        birthYear: extractYear(ind.birth_date),
        deathYear: extractYear(ind.death_date),
        birthPlace: ind.birth_place,
        photoUrl: (() => {
          const url = ind.profile_image_url ?? ind.photo_url;
          const backend = process.env.REACT_APP_BACKEND_URL || '';
          return url && !url.startsWith('http') ? `${backend}${url}` : url;
        })(),
        generation,
        childCount: children.length,
        isRoot: generation === 0
      }
    });
    
    // Add children
    let childOffset = xOffset;
    children.forEach(childId => {
      const childWidth = subtreeWidth[childId] || 1;
      
      edges.push({
        id: `${personId}-${childId}`,
        source: personId,
        target: childId,
        type: 'smoothstep',
        style: { stroke: COLORS.goldAccent, strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: COLORS.goldAccent }
      });
      
      addNode(childId, generation + 1, childOffset, x);
      childOffset += childWidth * nodeSpacing;
    });
  };
  
  addNode(rootId, 0, 0, 0);
  
  return { nodes, edges };
};

const DescendantChart = ({ 
  individuals = [], 
  relationships = [], 
  rootPersonId,
  onPersonClick
}) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!rootPersonId || individuals.length === 0) {
      return { nodes: [], edges: [] };
    }
    return buildDescendantLayout(individuals, relationships, rootPersonId);
  }, [individuals, relationships, rootPersonId]);
  
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((event, node) => {
    if (onPersonClick) {
      onPersonClick(node.id);
    }
  }, [onPersonClick]);
  
  if (!rootPersonId) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: COLORS.warmSand }}>
        <p className="text-lg" style={{ color: COLORS.heritageGreen }}>
          Select a person to view their descendants
        </p>
      </div>
    );
  }
  
  const rootPerson = individuals.find(i => i.id === rootPersonId);
  
  return (
    <div className="w-full h-full flex flex-col" style={{ backgroundColor: COLORS.warmSand }}>
      {/* Header */}
      <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(44, 74, 59, 0.2)' }}>
        <h3 className="text-lg font-semibold" style={{ color: COLORS.heritageGreen }}>
          Descendants of {rootPerson ? `${rootPerson.given_names || ''} ${rootPerson.surname || ''}`.trim() : 'Selected Person'}
        </h3>
        <p className="text-sm opacity-70" style={{ color: COLORS.heritageGreen }}>
          Family tree branching downward • Click any person to view details
        </p>
      </div>
      
      {/* Chart */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
        >
          <Background color={COLORS.heritageGreen} gap={20} size={1} style={{ opacity: 0.05 }} />
          <Controls 
            style={{ 
              backgroundColor: COLORS.heritageGreen,
              borderRadius: '8px',
              border: 'none'
            }}
          />
          <MiniMap 
            nodeColor={(node) => node.data?.gender === 'male' ? COLORS.maleNode : COLORS.femaleNode}
            style={{ backgroundColor: 'rgba(44, 74, 59, 0.1)' }}
          />
        </ReactFlow>
      </div>
    </div>
  );
};

export default DescendantChart;
